<script>
    import { Checkbox, Form, FormItem, Input } from 'element-ui';
    import { mapActions, mapMutations, mapState } from 'vuex';
    import Loader from '~/components/loader/components/loader';
    import Panel from '~/components/panel/components/panel';
    import PasswordStrength from '~/components/password_strength/components/password_strength';
    import TogglePasswordInput from '~/components/toggle_password_input/components/toggle_password_input';
    import vMediaQuery from 'v-media-query';
    import Vue from 'vue';
    import VueButton from '~/components/button/components/button';

    Vue.use(Checkbox);
    Vue.use(Form);
    Vue.use(FormItem);
    Vue.use(Input);
    Vue.use(vMediaQuery);

    export default {
        components: {
            Loader,
            Panel,
            PasswordStrength,
            TogglePasswordInput,
            VueButton,
        },

        data() {
            return {
                agreeWithTerms: false,
                emailIsValid: false,
                rules: {
                    email: [
                        { validator: this.validateEmail, trigger: 'blur'}
                    ],
                    confirmPassword: [
                        { validator: this.validatePasswordMatch, trigger: 'blur'}
                    ]
                },
                passwordHasFocus: false,
                confirmPasswordHasFocus: false,
                passwordIsValid: false
            }
        },

        computed: {
            ...mapState('newUserRegistrationForm', [
                'formData',
                'loading',
                'passwordRulesData',
                'prepopulatedForm'
            ]),

            formIsValid() {
                return (
                    this.emailIsValid
                    && this.formData.email.length >= 3
                    && this.agreeWithTerms === true
                    && this.formData.password.length > 0
                    && this.formData.password === this.formData.confirm_password
                    && this.passwordIsValid
                );
            },

            isMobile() {
                return this.$mq.resize && this.$mq.below(768);
            }
        },

        created() {
            this.updateLoading({ createNewAccount: false });
            if(this.formData.rel_key){
                this.formData.allows_profile_updates = 1;
            }
        },

        mounted() {
            if(this.formData.email) {
                this.$refs['accountForm'].validate();
            }
        },

        methods: {
            ...mapActions('newUserRegistrationForm', {
                checkEmailAddress: 'checkEmailAddress',
                createNewAccount: 'createNewAccount',
            }),

            ...mapMutations('newUserRegistrationForm', [
                'updateLoading',
            ]),

            validateEmail(rule, value, callback) {
                this.checkEmailAddress(value)
                .then(() => {
                    this.emailIsValid = true;
                    callback();
                })
                .catch(xhr => {
                    let message = xhr.responseJSON.error;
                    this.emailIsValid = false;
                    callback(new Error(message));
                });
            },

            validatePasswordMatch(rule, value, callback) {
                if(this.formData.confirm_password !== this.formData.password) {
                    callback(new Error('Passwords do not match'));
                }
            },

            handleSubmit() {
                this.$refs['accountForm'].validate();
                this.createNewAccount();
            },

            handlePasswordFocus() {
                this.passwordHasFocus = true;
            },

            handlePasswordBlur() {
                this.passwordHasFocus = false;
            },

            handlePasswordInputChange(elm, value) {
                if(elm === 'confirmPassword'){
                    this.$refs['accountForm'].validate();
                }

                // If user has already entered confirm password and then decides to change password
                if(elm === 'password' && this.formData.confirm_password.length > 0){
                    this.$refs['accountForm'].validate();
                }
            },

            handlePasswordChange(value) {
                this.passwordIsValid = value;
            }
        }
    }
</script>
<template>
    <transition
        name="custom-classes-transition"
        enter-active-class="animated fadeInDown"
    >
        <div>
            <div class="registration-form-top-area">
                <h3>Let's get started setting up your Total Expert account.</h3>
                <br />
                <h4>Already have an account? Click <strong><a href="/login">here</a></strong> to sign in.</h4>
            </div>
            <el-form
                :model="formData"
                :rules="rules"
                ref="accountForm"
                label-position="top"
                autocomplete="off"
            >
                <loader :is-loading="loading.createNewAccount"></loader>
                <panel title="Please enter information to set up your account.">
                    <div slot="body">
                        <div class="row">
                            <div class="col-xs-12" v-if="prepopulatedForm">
                                <p><strong>Some information has been pre-populated for you.</strong></p>
                            </div>

                            <div class="col-xs-12 col-sm-6">
                                <el-form-item
                                    label="Email Address"
                                    prop="email"
                                    maxlength="50">
                                    <el-input
                                        v-model="formData.email">
                                    </el-input>
                                </el-form-item>

                                <toggle-password-input
                                    prop-name="password"
                                    label-name="Create Total Expert Password"
                                    v-model="formData.password"
                                    @input="handlePasswordInputChange('password', $event)">
                                </toggle-password-input>

                                <toggle-password-input
                                    prop-name="confirmPassword"
                                    label-name="Confirm Total Expert Password"
                                    v-model="formData.confirm_password"
                                    @input="handlePasswordInputChange('confirmPassword', $event)">
                                </toggle-password-input>
                            </div>

                            <div class="col-xs-12 col-sm-6">
                                <div class="col-sm-12 p-0">
                                    <password-strength
                                        optional-class="password-requirements-wrap--registration"
                                        :rules="passwordRulesData.rules"
                                        :value="formData.password"
                                        @password-changed="handlePasswordChange">
                                    </password-strength>
                                </div>
                            </div>

                            <div class="col-xs-12 col-md-12">
                                <el-checkbox
                                    name="collab_approval"
                                    v-model="formData.collab_approval"
                                    true-label="1"
                                    false-label="0"
                                >
                                </el-checkbox>
                                <span class="checkbox-label">I authorize collaboration partners to edit my profile information (Collaboration partners will not have access to your contacts).</span>

                            </div>
                        </div>
                    </div>
                    <div slot="footer">
                        <div class="row">
                            <div class="col-xs-12 col-sm-8 col-md-8">
                                <el-checkbox
                                    v-model="agreeWithTerms"
                                >
                                </el-checkbox>
                                <span class="checkbox-label">I have read and agree with the <strong><a href="http://totalexpert.com/terms-of-service" target="_blank" data-automation-id="terms-of-service">Terms of Service</a></strong></span>
                            </div>
                            <div class="col-xs-12 col-sm-4 col-md-4">
                                <vue-button type="primary" @click="handleSubmit" :disabled="! formIsValid">
                                    Create Account
                                </vue-button>
                            </div>
                        </div>
                    </div>
                </panel>
            </el-form>
        </div>
    </transition>
</template>
<style scoped>
    .registration-form-top-area {
        padding: 60px 0 30px 0;
        text-align: center;
        color: inherit;
    }

    .checkbox-label {
        display: inline-block;
        vertical-align: top;
        max-width:89%;
    }

    button.btn {
        width: 100%;
    }
</style>